import React, { useMemo, useState } from "react";
import Badge from "../../../library/Badge";
import Banner from "../../../library/Banner";
import IconButton from "../../../library/IconButton";
import { getGroupLabelForModuleType } from "./studyPaths.utils";
import {
    StyledCardBody,
    StyledDataSortIcon,
    StyledDataTable,
    StyledDataTd,
    StyledDataTh,
    StyledGroupBandLeft,
    StyledGroupBandRow,
    StyledPanelBandHeader,
    StyledRowActions,
    StyledRowLabel,
    StyledSectionHeading,
    StyledTableCard,
    StyledTableWrapper,
    StyledWarningDot,
} from "./studyPaths.styles";

// Fixed widths for the same reason as PhaseModuleGroupsTable's — Name takes the remainder.
const COLUMNS = [
    { key: "name", label: "Name" },
    { key: "code", label: "Code", width: "104px" },
    { key: "duration", label: "Duration", width: "100px" },
    { key: "credits", label: "Credits", width: "92px" },
    { key: "group", label: "Assign Module", width: "159px", sortable: false },
    { key: "action", label: "Action", width: "67px", sortable: false },
];

/**
 * Rows here keep their raw casing (module.Code/Duration) rather than going through
 * normalizeModuleRows, since the row rendering below still reads module.Id/.type/.disabledReason
 * directly — so sorting reads the same raw fields rather than a normalized shape.
 */
const COLUMN_ACCESSORS = {
    name: (module) => module.name,
    code: (module) => module.Code,
    duration: (module) => module.Duration,
    credits: (module) => module.credits,
};

/**
 * "Unassigned Modules" quality-check panel — modules linked to the programme version but not
 * placed into any phase. Shared by SPEC1 R4 (Study Phases list) and SPEC3 R3 (phase form).
 * @param {Array} props.modules Unassigned module rows.
 * @param {(module: object) => void} props.onAssign Assigns a module into the current context
 *   (selected phase on SPEC1, this phase on SPEC3) using the module's own group.
 */
function UnassignedModulesPanel({ modules = [], loading = false, error = false, onAssign, disabled = false, disabledReason }) {
    const [sortKey, setSortKey] = useState("name");
    const [sortAsc, setSortAsc] = useState(true);

    const sortedModules = useMemo(() => {
        const accessor = COLUMN_ACCESSORS[sortKey];
        const sorted = [...modules].sort((left, right) => (
            String(accessor(left) ?? "").localeCompare(String(accessor(right) ?? ""), undefined, { numeric: true })
        ));
        return sortAsc ? sorted : sorted.reverse();
    }, [modules, sortKey, sortAsc]);

    const handleSort = (columnKey) => {
        if (columnKey === sortKey) {
            setSortAsc((current) => !current);
            return;
        }
        setSortKey(columnKey);
        setSortAsc(true);
    };

    return (
        <StyledTableCard>
            <StyledPanelBandHeader>
                <StyledGroupBandRow>
                    <StyledGroupBandLeft>
                        <StyledSectionHeading>Unassigned Modules</StyledSectionHeading>
                        {modules.length > 0 && (
                            <Badge tone="warning" icon="exclamation-triangle">
                                {modules.length} Unassigned
                            </Badge>
                        )}
                    </StyledGroupBandLeft>
                </StyledGroupBandRow>
            </StyledPanelBandHeader>

            {loading ? (
                <StyledCardBody>Loading unassigned modules...</StyledCardBody>
            ) : error ? (
                <StyledCardBody>
                    <Banner tone="warning">
                        Unassigned modules couldn't be loaded, so this check is incomplete — an empty
                        result here doesn't mean every module is assigned. Reload to try again.
                    </Banner>
                </StyledCardBody>
            ) : modules.length === 0 ? (
                <StyledCardBody>
                    <Banner tone="success">
                        All modules assigned — every module in this programme version is assigned to
                        a study path phase. No action needed.
                    </Banner>
                </StyledCardBody>
            ) : (
                <StyledTableWrapper>
                    <StyledDataTable>
                        <thead>
                            <tr>
                                {COLUMNS.map((column) => (
                                    <StyledDataTh
                                        key={column.key}
                                        $width={column.width}
                                        $sortable={column.sortable !== false}
                                        onClick={column.sortable === false ? undefined : () => handleSort(column.key)}
                                    >
                                        {column.label}
                                        {column.sortable !== false && (
                                            <StyledDataSortIcon
                                                $active={sortKey === column.key}
                                                className={`fal ${sortKey === column.key ? `fa-angle-${sortAsc ? "up" : "down"}` : "fa-angles-up-down"}`}
                                                aria-hidden="true"
                                            />
                                        )}
                                    </StyledDataTh>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedModules.map((module) => {
                                const rowDisabled = disabled || Boolean(module.disabledReason);
                                return (
                                    <tr key={module.Id}>
                                        <StyledDataTd>
                                            <StyledRowLabel $muted={rowDisabled}>{module.name}</StyledRowLabel>
                                            {module.disabledReason && (
                                                <StyledWarningDot
                                                    className="fal fa-exclamation-circle"
                                                    aria-hidden="true"
                                                    title={module.disabledReason}
                                                />
                                            )}
                                        </StyledDataTd>
                                        <StyledDataTd><StyledRowLabel $muted={rowDisabled}>{module.Code}</StyledRowLabel></StyledDataTd>
                                        <StyledDataTd><StyledRowLabel $muted={rowDisabled}>{module.Duration ? `${module.Duration} Weeks` : "-"}</StyledRowLabel></StyledDataTd>
                                        <StyledDataTd><StyledRowLabel $muted={rowDisabled}>{module.credits}</StyledRowLabel></StyledDataTd>
                                        <StyledDataTd><StyledRowLabel $muted={rowDisabled}>{getGroupLabelForModuleType(module.type)}</StyledRowLabel></StyledDataTd>
                                        <StyledDataTd>
                                            <StyledRowActions>
                                                <IconButton
                                                    icon="plus"
                                                    ariaLabel={`Assign ${module.name}`}
                                                    title={`Assign ${module.name} to ${getGroupLabelForModuleType(module.type)}`}
                                                    variant="solid"
                                                    disabled={rowDisabled}
                                                    disabledReason={module.disabledReason || disabledReason}
                                                    onClick={() => onAssign(module)}
                                                />
                                            </StyledRowActions>
                                        </StyledDataTd>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </StyledDataTable>
                </StyledTableWrapper>
            )}
        </StyledTableCard>
    );
}

UnassignedModulesPanel.displayName = "UnassignedModulesPanel";

export default UnassignedModulesPanel;
